import { useState } from "react";
import { validateAadhaar, getAadhaarHelperText } from "../../../helpers/abdm-helper";

/**
 * Custom React hook to handle Photo ID input formatting and format validation
 * for various ID types, mirroring the logic in Pre-registration-uwin.js.
 *
 * @param {Array} photoIdTypes - List of fetched photo ID types
 * @param {string} selectedPhotoIdType - Currently selected photo ID type (encrypted ID)
 * @param {string} photoIdNumber - Current photo ID number value
 * @param {Function} setPhotoIdNumber - Setter function for photo ID number
 */
export const usePhotoId = (photoIdTypes, selectedPhotoIdType, photoIdNumber, setPhotoIdNumber) => {
  const [photoIdError, setPhotoIdError] = useState("");

  // Helper to find the plain text type name of the currently selected Photo ID Type
  const getPhotoIdTypeName = () => {
    if (!photoIdTypes || photoIdTypes.length === 0) return "";
    const selectedOption = photoIdTypes.find((opt) => opt.type === selectedPhotoIdType);
    return selectedOption?.photo_type || "";
  };

  const photoIdTypeName = getPhotoIdTypeName();
  const isAadhaarSelected = photoIdTypeName?.toLowerCase().trim() === "aadhaar card";

  const { isValid: isAadhaarValid, cleanAadhaar: cleanedAadhaar } = isAadhaarSelected
    ? validateAadhaar(photoIdNumber)
    : { isValid: true, cleanAadhaar: "" };

  const handlePhotoIdNumberChange = (e) => {
    let value = e.target.value;

    if (isAadhaarSelected) {
      value = value.replace(/\D/g, ""); // Remove non-digits
      if (value.length > 12) value = value.slice(0, 12);

      // Add hyphens dynamically: XXXX-XXXX-XXXX
      if (value.length > 8) {
        value = value.slice(0, 4) + "-" + value.slice(4, 8) + "-" + value.slice(8);
      } else if (value.length > 4) {
        value = value.slice(0, 4) + "-" + value.slice(4);
      }
    }
    setPhotoIdNumber(value);
    setPhotoIdError(""); // Clear validation error on type
  };

  // Validate the entered photo ID number format against its type
  const validatePhotoIdNumber = (isPrepopulated = false) => {
    let errorMsg = "";

    if (!selectedPhotoIdType) {
      errorMsg = "Photo ID required";
    } else if (!photoIdNumber) {
      errorMsg = "Photo ID number required";
    } else if (isPrepopulated) {
      // If prepopulated from backend and disabled, treat existing record as valid
      errorMsg = "";
    } else {
      const typeName = photoIdTypeName?.toLowerCase().trim() || "";

      if (typeName === "aadhaar card") {
        if (!isAadhaarValid) {
          const helperText = getAadhaarHelperText(photoIdNumber, cleanedAadhaar, isAadhaarValid);
          errorMsg = helperText || "Invalid Aadhaar number";
        }
      } else if (typeName === "passport") {
        if (!/^[A-Z][0-9]{7}$/.test(photoIdNumber)) {
          errorMsg = "Invalid Passport number (1 letter + 7 digits)";
        }
      } else if (typeName === "driving license") {
        if (!/^[A-Z0-9]{8,15}$/i.test(photoIdNumber)) {
          errorMsg = "Invalid Driving License number";
        }
      } else if (typeName === "pan card") {
        if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(photoIdNumber)) {
          errorMsg = "Invalid PAN number (Format: ABCDE1234F)";
        }
      } else if (typeName === "abha id") {
        if (!/^\d{2}-\d{4}-\d{4}-\d{4}$/.test(photoIdNumber)) {
          errorMsg = "Invalid ABHA ID (Format: 12-3456-7890-1234)";
        }
      } else if (typeName === "birth certificate") {
        // eslint-disable-next-line no-useless-escape
        if (!/^[A-Z0-9\-\/]{5,20}$/i.test(photoIdNumber)) {
          errorMsg = "Invalid Birth Certificate number";
        }
      }
    }

    setPhotoIdError(errorMsg);
    return {
      isValid: !errorMsg,
      errorMsg,
    };
  };

  return {
    handlePhotoIdNumberChange,
    validatePhotoIdNumber,
    photoIdError,
    setPhotoIdError,
    isAadhaarSelected,
    isAadhaarValid,
    getAadhaarHelperText: () => getAadhaarHelperText(photoIdNumber, cleanedAadhaar, isAadhaarValid),
  };
};
